import React, { useState } from 'react';
import { RotateCcw, AlertTriangle, X } from 'lucide-react';
import { tallyResetApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import DateInput from '../components/DateInput/DateInput';

/**
 * Admin screen for the Tally master-sync feeds. Each row clears the sync stamp
 * on one master so every row re-appears in its /tally-pending feed and gets
 * re-created in Tally — the fix after the Tally company is recreated or an
 * import goes wrong. Vouchers additionally take a period so only that range is
 * re-sent; leaving both dates blank resets every voucher.
 *
 * The order below is the import order Tally needs (groups → ledgers → items →
 * transactions), so resetting top-to-bottom re-sends a consistent set.
 */

type TargetKey = 'ledgerGroups' | 'customers' | 'itemGroups' | 'items' | 'vouchers';

interface Target {
  key: TargetKey;
  label: string;
  feed: string;
  description: string;
}

const TARGETS: Target[] = [
  {
    key: 'ledgerGroups',
    label: 'Ledger Groups',
    feed: 'GET /api/ledger-groups?pending=1',
    description: 'Every ledger group is queued for import again.',
  },
  {
    key: 'customers',
    label: 'Ledgers (Customers)',
    feed: 'GET /api/customers/tally-pending',
    description: 'Every customer ledger master, with opening bills, is queued again.',
  },
  {
    key: 'itemGroups',
    label: 'Item Groups',
    feed: 'GET /api/items/groups/tally-pending',
    description: 'Every stock group is queued for import again.',
  },
  {
    key: 'items',
    label: 'Items',
    feed: 'GET /api/items/tally-pending',
    description: 'Every stock item, with opening batches, is queued again.',
  },
  {
    key: 'vouchers',
    label: 'Vouchers',
    feed: 'GET /api/vouchers/tally-pending',
    description: 'Vouchers in the chosen period are queued for import again.',
  },
];

const TallyReset: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [confirming, setConfirming] = useState<Target | null>(null);
  const [running, setRunning] = useState<TargetKey | null>(null);
  const [lastReset, setLastReset] = useState<Partial<Record<TargetKey, number>>>({});

  // The pickers hold YYYY-MM-DD (what the API wants); show DD/MM/YYYY.
  const shown = (iso: string) => iso.split('-').reverse().join('/');

  const periodLabel = () => {
    if (dateFrom && dateTo) return `${shown(dateFrom)} to ${shown(dateTo)}`;
    if (dateFrom) return `on/after ${shown(dateFrom)}`;
    if (dateTo) return `on/before ${shown(dateTo)}`;
    return 'all dates';
  };

  const runReset = async (target: Target) => {
    setConfirming(null);
    setRunning(target.key);
    try {
      const res = target.key === 'vouchers'
        ? await tallyResetApi.vouchers({ date_from: dateFrom || undefined, date_to: dateTo || undefined })
        : await tallyResetApi[target.key]();
      setLastReset(prev => ({ ...prev, [target.key]: res.reset }));
      showSuccess('Reset done', `${res.reset} ${target.label.toLowerCase()} row(s) will be sent to Tally again.`);
    } catch (err: any) {
      showError('Reset failed', err?.message || 'Could not reset the Tally sync state.');
    }
    setRunning(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[900px] mx-auto">
        <h1 className="text-lg font-semibold text-gray-800">Reset Tally Data</h1>
        <p className="text-sm text-gray-500 mt-1">
          Clears the &ldquo;already sent to Tally&rdquo; flag so the data shows up in the pending feed again.
          Nothing in the portal is deleted or changed — only the sync status.
        </p>

        <div className="flex items-start gap-2 mt-3 p-3 rounded border border-amber-200 bg-amber-50 text-[13px] text-amber-800">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            Run these only when Tally should re-import from scratch. If the same records still exist in Tally,
            re-importing them will create duplicates there. Import in the order shown below.
          </span>
        </div>

        {/* Voucher period — only applies to the Vouchers row */}
        <div className="mt-4 p-3 rounded border border-gray-200 bg-gray-50">
          <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2">Voucher period</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-[150px]">
              <DateInput value={dateFrom} onChange={setDateFrom} placeholder="From date" />
            </div>
            <div className="w-[150px]">
              <DateInput value={dateTo} onChange={setDateTo} placeholder="To date" />
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                <X size={13} /> Clear
              </button>
            )}
            <span className="text-xs text-gray-500">
              Leave blank to reset every voucher. Master resets below ignore this period.
            </span>
          </div>
        </div>

        <div className="mt-4 divide-y divide-gray-100 border border-gray-200 rounded">
          {TARGETS.map((t, idx) => (
            <div key={t.key} className="flex items-center gap-3 p-3">
              <span className="w-6 text-center text-xs text-gray-400">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">{t.label}</div>
                <div className="text-xs text-gray-500">
                  {t.description}
                  {t.key === 'vouchers' && <span className="text-gray-600"> Period: {periodLabel()}.</span>}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">{t.feed}</div>
              </div>
              {lastReset[t.key] !== undefined && (
                <span className="text-xs text-green-600 whitespace-nowrap">{lastReset[t.key]} reset</span>
              )}
              <button
                onClick={() => setConfirming(t)}
                disabled={running !== null}
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-medium rounded px-3 py-1.5 whitespace-nowrap"
              >
                <RotateCcw size={14} className={running === t.key ? 'animate-spin' : ''} />
                {running === t.key ? 'Resetting...' : 'Reset'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={18} className="text-red-500" />
              <h2 className="text-base font-semibold text-gray-800">Reset {confirming.label}?</h2>
            </div>
            <p className="text-sm text-gray-600">
              {confirming.key === 'vouchers'
                ? `All vouchers (${periodLabel()}) will be marked as never sent to Tally and will re-appear in the pending feed.`
                : `All ${confirming.label.toLowerCase()} will be marked as never sent to Tally and will re-appear in the pending feed.`}
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirming(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button onClick={() => runReset(confirming)}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded">Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TallyReset;
